// Pure adapter for the Home "Portfolio in motion" pane.
//
// Rows are RAW projects (same fields as the Projects landing) plus legacy
// adapter fields. Target and Seats come only from the canonical selectors in
// features/projects/portfolioRowModel.js; this file keeps no seat or target
// calculation of its own. Eligibility, Target ordering and the ten-row cap
// live here; presentation is the shared ProjectsPortfolioList.

import { T, PROJECT_PHASE, PROJECT_STATUS, PROJECT_TYPE } from "../../app/tokens.js";
import { comparePortfolioTargets, selectProjectSeats, selectProjectTarget } from "../../features/projects/portfolioRowModel.js";

const MOTION_PHASES = new Set(["planning", "design", "procurement", "build", "move_launch"]);
const CAPACITY_PROJECT_TYPES = new Set(["new_build", "renovation", "exit", "consolidation"]);
const MAX_VISIBLE_ROWS = 10;

function asDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = asDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function officeLabel(project, includeMarketNames) {
  const office = project.locationAlias ?? project.locationId;
  const market = includeMarketNames ? project.locationName : null;
  return [market, office].filter(Boolean).join(" · ") || "Office not linked";
}

function earliestMilestoneFor(project, milestones) {
  return (milestones ?? [])
    .filter((milestone) => milestone?.projectId === project.id && milestone.status !== "complete" && !milestone.deletedAt)
    .sort((a, b) => {
      const ad = asDate(a.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bd = asDate(b.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return ad - bd;
    })[0] ?? null;
}

function nextMilestoneFor(milestone) {
  if (!milestone) return { label: "No upcoming milestone", dueDate: null, dateLabel: "—" };
  return {
    label: milestone.name || "Upcoming milestone",
    dueDate: milestone.dueDate ?? null,
    dateLabel: formatDate(milestone.dueDate) ?? "Date TBD",
  };
}

/**
 * Active capacity-moving projects for Home, Target ascending, capped at ten.
 * `markets`/`today` are accepted for call-site compatibility only.
 */
export function buildPortfolioMotionModel({
  projects = [],
  milestones = [],
  milestonesDegraded = false,
  planDegraded = false,
  projectsDegraded = false,
  includeMarketNames = true,
} = {}) {
  const rows = [];

  for (const project of Array.isArray(projects) ? projects : []) {
    if (
      !project?.id
      || project.deletedAt
      || !MOTION_PHASES.has(project.phase)
      || !CAPACITY_PROJECT_TYPES.has(project.projectType)
    ) continue;

    const rawMilestone = milestonesDegraded ? null : earliestMilestoneFor(project, milestones);
    const milestone = nextMilestoneFor(rawMilestone);
    const target = selectProjectTarget(project);
    const targetDate = asDate(target?.date);
    const seatDelta = selectProjectSeats(project);
    const type = PROJECT_TYPE[project.projectType] ?? { label: project.projectType || "Project", color: T.mid, bg: T.lightGray };
    const status = PROJECT_STATUS[project.statusOverall] ?? { label: "No status" };

    rows.push({
      ...project,
      // Home suppresses market names; mirror that on the shared row's
      // secondary line, which reads locationName.
      ...(includeMarketNames ? null : { locationName: null }),
      nextMilestone: rawMilestone,
      milestonesError: Boolean(milestonesDegraded),
      // Legacy adapter fields (kept for existing consumers/tests).
      projectName: project.name || "Unnamed project",
      officeName: officeLabel(project, includeMarketNames),
      summary: project.oneLineSummary ?? null,
      typeLabel: type.label,
      typeColor: type.color,
      typeBg: type.bg,
      phaseLabel: PROJECT_PHASE[project.phase]?.label ?? project.phase,
      milestoneName: milestone.label,
      milestoneDate: milestone.dateLabel,
      milestoneDueDate: milestone.dueDate,
      targetDate: formatDate(targetDate) ?? "Data gap",
      targetDueDate: targetDate,
      targetLabel: target?.label,
      targetDataGap: targetDate == null,
      seatDelta,
      seatDeltaState: seatDelta == null ? "data_gap" : "available",
      status: project.statusOverall,
      statusLabel: status.label,
      statusColor: status.color,
      statusBg: status.bg,
      healthKey: project.statusOverall === "on_track" ? "green"
        : project.statusOverall === "at_risk" ? "yellow"
        : project.statusOverall === "critical" ? "red" : null,
      lastUpdated: project.lastUpdated ?? null,
    });
  }

  rows.sort((a, b) => comparePortfolioTargets(a, b, 1));

  const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);
  const incomingSeats = visibleRows.reduce((sum, row) => sum + Math.max(0, row.seatDelta ?? 0), 0);
  const outgoingSeats = visibleRows.reduce((sum, row) => sum + Math.max(0, -(row.seatDelta ?? 0)), 0);

  return {
    rows: visibleRows,
    projectCount: visibleRows.length,
    incomingSeats,
    outgoingSeats,
    pipelineNetSeats: incomingSeats - outgoingSeats,
    hasDegradedData: Boolean(milestonesDegraded || planDegraded || projectsDegraded),
  };
}

/**
 * Build the read-only project activity model for one office.
 *
 * This intentionally shares the Portfolio in motion adapter's canonical
 * project metadata and milestone formatting, while leaving the Home model's
 * capacity-specific filtering and impact calculations untouched.
 */
export function buildOfficeProjectsActivityModel({
  projects = [],
  milestones = [],
  locationId,
  limit = 3,
} = {}) {
  const rows = [];

  for (const project of Array.isArray(projects) ? projects : []) {
    if (
      !project?.id
      || project.deletedAt
      || project.phase === "complete"
      || project.locationId !== locationId
    ) continue;

    const milestone = (Array.isArray(milestones) ? milestones : [])
      .filter((item) => item?.projectId === project.id && item.status !== "complete")
      .sort((a, b) => {
        const ad = asDate(a.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bd = asDate(b.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return ad - bd;
      })[0] ?? null;
    const nextMilestone = nextMilestoneFor(milestone);
    const type = PROJECT_TYPE[project.projectType] ?? {
      label: project.projectType || "Project",
      color: T.mid,
      bg: T.lightGray,
    };
    const status = PROJECT_STATUS[project.statusOverall] ?? {
      label: "No status",
      color: T.mid,
      bg: T.lightGray,
    };

    rows.push({
      id: project.id,
      projectName: project.name || "Unnamed project",
      summary: project.oneLineSummary ?? null,
      projectType: project.projectType,
      typeLabel: type.label,
      typeColor: type.color,
      typeBg: type.bg,
      milestoneName: nextMilestone.label,
      milestoneDate: nextMilestone.dateLabel,
      milestoneDueDate: nextMilestone.dueDate,
      status: project.statusOverall,
      statusLabel: status.label,
      statusColor: status.color,
      statusBg: status.bg,
    });
  }

  rows.sort((a, b) => {
    const ad = asDate(a.milestoneDueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bd = asDate(b.milestoneDueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return ad - bd
      || a.projectName.localeCompare(b.projectName)
      || String(a.id).localeCompare(String(b.id));
  });

  return {
    rows: rows.slice(0, Math.max(0, limit)),
    projectCount: rows.length,
    atRiskCount: rows.filter((row) => row.status === "at_risk" || row.status === "critical").length,
  };
}

export function formatMotionSeats(value) {
  if (value == null) return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  if (number === 0) return "0";
  return number > 0 ? `+${number}` : `−${Math.abs(number)}`;
}
